import { DEFAULT_LANGUAGE } from '@app/src/wf-constants'
import { SUPPORTED_LANGUAGES } from '@app/src/wf-constants/languages'

export type TranslationMessages = Record<string, string>

type NestedTranslations = { [key: string]: string | NestedTranslations }

export const CACHED_LOCALE_STORAGE_KEY = 'cached_locale'

const jsonFileNameByLocale: Record<string, string> = Object.fromEntries(
  SUPPORTED_LANGUAGES.map(({ localeName, jsonFileName }) => [localeName, jsonFileName]),
)

export const languagesWeHaveTranslationFilesFor = Object.keys(jsonFileNameByLocale)

const languageWithoutRegionCode = (language: string): string => language.toLowerCase().split(/[_-]+/)[0]

const supportedBaseLanguages = new Set(languagesWeHaveTranslationFilesFor.map(languageWithoutRegionCode))

/**
 * Get the locale to format dates and numbers with.
 * The region code is kept when we support the base language, so an 'de-AT' user
 * gets Austrian formatting while reading the German translations.
 * @param language The language to find a compatible locale for
 * @returns {*} A compatible locale based on the language or the default locale
 */
export const getLocale = (language = ''): string =>
  supportedBaseLanguages.has(languageWithoutRegionCode(language)) ? language : DEFAULT_LANGUAGE

/**
 * Map a language onto the locale we actually ship a translation file for.
 * getLocale can return a region variant we have no file for (e.g. 'de-AT'),
 * so region codes are stripped before falling back to the default language.
 */
const resolveTranslationFileLocale = (language = ''): string => {
  if (jsonFileNameByLocale[language]) {
    return language
  }

  const withoutRegionCode = languageWithoutRegionCode(language)
  const matchingLocale = languagesWeHaveTranslationFilesFor.find(
    locale => languageWithoutRegionCode(locale) === withoutRegionCode,
  )

  return matchingLocale ?? DEFAULT_LANGUAGE
}

/**
 * Flatten nested translation objects into dot-separated keys,
 * which is the shape react-intl expects.
 */
const flattenTranslations = (translations: NestedTranslations): TranslationMessages => {
  const flattened: TranslationMessages = {}

  const visit = (node: NestedTranslations, prefix: string): void => {
    Object.entries(node).forEach(([key, value]) => {
      const path = prefix ? `${prefix}.${key}` : key
      if (value !== null && typeof value === 'object') {
        visit(value, path)
      } else {
        flattened[path] = value
      }
    })
  }

  visit(translations, '')

  return flattened
}

const messagesByLocale: Record<string, TranslationMessages> = {}
const pendingMessagesByLocale: Record<string, Promise<TranslationMessages>> = {}

const importTranslationFile = async (locale: string): Promise<TranslationMessages> => {
  const module = await import(`./locales/${jsonFileNameByLocale[locale]}.json`)
  const messages = flattenTranslations(module.default)
  messagesByLocale[locale] = messages
  return messages
}

/**
 * Load the translations for a language, reusing an in-flight or completed load.
 * A failed load is evicted so a later call can retry, and falls back to the
 * default language so a missing translation file cannot take the app down.
 */
export const loadMessages = (language = ''): Promise<TranslationMessages> => {
  const locale = resolveTranslationFileLocale(language)

  pendingMessagesByLocale[locale] ??= importTranslationFile(locale).catch(error => {
    delete pendingMessagesByLocale[locale]

    if (locale === DEFAULT_LANGUAGE) {
      throw error
    }

    console.error(`Could not load translations for ${locale}, falling back to ${DEFAULT_LANGUAGE}`, error)
    return loadMessages(DEFAULT_LANGUAGE)
  })

  return pendingMessagesByLocale[locale]
}

/**
 * Synchronous access to already-loaded translations.
 * Falls back to the default language, which is always loaded, so callers
 * never receive undefined while another locale is still loading.
 */
export const getMessages = (language = ''): TranslationMessages =>
  messagesByLocale[resolveTranslationFileLocale(language)] ?? messagesByLocale[DEFAULT_LANGUAGE] ?? {}

const cachedLocale = localStorage.getItem(CACHED_LOCALE_STORAGE_KEY) ?? DEFAULT_LANGUAGE

// The default language is always loaded: it backs getMessages' fallback and the
// always-English email previews. Loading it alongside the cached locale means a
// cold start fetches at most two translation files instead of all of them.
//
// allSettled so a failed load cannot stop the module from evaluating: react-intl
// falling back to message ids is recoverable, but a rejected entry module never
// mounts the error boundary that reloads on missing chunks.
await Promise.allSettled([loadMessages(DEFAULT_LANGUAGE), loadMessages(cachedLocale)])
